/**
 * Back In Stock Modal
 * A lightweight modal to collect customer email for back-in-stock notifications.
 */
class BackInStockModal {
  /**
   * @param {Object} variant - Selected variant info
   * @param {HTMLElement} container - Host element to attach the modal
   * @param {Object} productInfo - { productId, handle, title }
   * @param {Function} [callback] - Optional callback after closing via the close button
   */
  constructor(variant, container, productInfo, callback) {
    this.localeData = window._erj_?.getGlobalLocaleStrings()?.product_item || {};
    this.variant = variant;
    this.container = container;
    this.productInfo = productInfo;
    this.callback = typeof callback === "function" ? callback : null;
    this.images = {
      main: "",
      second: "",
      model: "",
    };

    this.showBackInStockModal();
  }

  /**
   * Prepare and render modal, attach to DOM, then bind events
   */
  showBackInStockModal() {
    const { mainImg, secondImg, modelImg, initialModalImg } = this.getInitialImages();
    this.images = { main: mainImg, second: secondImg, model: modelImg };

    // Build modal element
    this.modal = this.buildModal(initialModalImg);
    const modal = this.modal;

    // Prefill email input if available
    const emailInput = modal.querySelector(".back-in-stock-email");
    if (emailInput) {
      emailInput.value = (window._erj_?.customer?.email || "") + "";
    }

    document.body.appendChild(modal);    

    // Prevent default navigation under the modal
    this.preventDefaultNav();

    // Bind events
    this.bindEvents();
  }

  /**
   * Derive best available images from container/variant
   */
  getInitialImages() {
    const mainImg = this.productInfo.images?.[0] || "";
    const secondImg = this.productInfo.images?.[1] || "";
    const modelImg = this.variant?.image || secondImg || "";
    const initialModalImg = modelImg || "";
    return { mainImg, secondImg, modelImg, initialModalImg };
  }

  /**
   * Create modal element with initial markup
   * @param {string} initialModalImg
   * @returns {HTMLElement}
   */
  buildModal(initialModalImg) {
    const modal = document.createElement("div");
    modal.className = "product-size-picker-modal-bg modal-back-in-stock";
    modal.innerHTML = `
      <div class="product-size-picker">
        <div class="modal-back-in-stock-image">
          <img loading="eager" fetchpriority="high" src="${initialModalImg}" alt="${this.productInfo.title}" />
        </div>
        <div class="modal-back-in-stock-content">
          <div class="product-size-picker-header">
            <div class="product-size-picker-step"></div>
            <div class="product-size-picker-header-title">${this.localeData.out_of_stock}</div>
            <div class="product-size-picker-close" style="cursor:pointer;"></div>
          </div>
          <div class="back-in-stock-form">
            <div class="back-in-stock-title">${this.localeData.notify}</div>
            <div class="popup-fields-row w-full">
              <div class="popup-field">
                <label>${this.localeData.email_placeholder}</label>
                <input id="${this.productInfo.productId}" type="email" class="back-in-stock-email" placeholder="${this.localeData.email_placeholder}" />
              </div>
            </div>
            <div>
              <button class="back-in-stock-submit" type="button">${this.localeData.get_notification}</button>
              <span class="back-in-stock-agree-term">${this.localeData.agree_terms}</span>
            </div>
          </div>
        </div>
      </div>
    `;
    return modal;
  }

  /**
   * Wire up close, input, and submit events
   */
  bindEvents() {
    const modal = this.modal;
    const closeBtn = modal.querySelector(".product-size-picker-close");
    const emailInput = modal.querySelector(".back-in-stock-email");
    const submitBtn = modal.querySelector(".back-in-stock-submit");

    // Close button
    closeBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.callback) this.callback();
      this.teardown();
    });

    // Prevent propagation on input and button
    const stopEvents = [
      "click",
      "mousedown",
      "mouseup",
      "touchstart",
      "touchend",
      "pointerdown",
      "pointerup",
      "focus",
    ];
    stopEvents.forEach((ev) => {
      emailInput.addEventListener(ev, (e) => e.stopPropagation(), { capture: false });
      submitBtn.addEventListener(ev, (e) => e.stopPropagation(), { capture: false });
    });

    // Enter to submit
    emailInput.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Enter") {
        e.preventDefault();
        submitBtn.click();
      }
    });


    // Autofocus email
    setTimeout(() => {
      emailInput?.focus();
    }, 0);

    // Submit
    submitBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const email = emailInput.value.trim();
      if (!this.validateEmail(email)) {
        alert("Subscription failed. Please try again.");
        this.teardown();
        return;
      }
      this.subscribe(email);
    });
  }

  /**
   * Basic email validation (preserve original behavior)
   * @param {string} email
   * @returns {boolean}
   */
  validateEmail(email) {
    return email.length > 0 && /\S+@\S+\.\S+/.test(email);
  }

  /**
   * Execute subscription flow with external handler
   * @param {string} email
   */
  subscribe(email) {
    const modal = this.modal;
    executeBISSubscribtionProcedureForUseForExampleOnProductItemAndOtherPlacesIfNeeded(
      this.productInfo.productId,
      this.variant?.id,
      this.productInfo.handle,
      email,
      () => {
        // Success UI
        const imgEl = modal.querySelector(".modal-back-in-stock-image img");
        const bestAfter = this.variant?.image || this.images.model || this.images.second || this.images.main || imgEl?.src || "";
        if (imgEl && bestAfter) imgEl.src = bestAfter;

        modal.querySelector(".product-size-picker-header-title").textContent = "";

        const formContainer = modal.querySelector(".back-in-stock-form");
        formContainer.innerHTML = `
          <div class="back-in-stock-all-set">${this.localeData.all_set || "You're all set!"}</div>
          <div class="back-in-stock-success">${this.localeData.thank_you_notify}</div>
          <button class="back-in-stock-submit" type="button">${this.localeData.got_it}</button>
        `;
        const doneBtn = formContainer.querySelector(".back-in-stock-submit");
        ["click", "touchstart", "pointerdown"].forEach((ev) => {
          doneBtn.addEventListener(ev, (evt) => evt.stopPropagation(), { capture: false });
        });
        doneBtn.addEventListener("click", (evt) => {
          evt.preventDefault();
          evt.stopPropagation();
          this.teardown();
        });
      },
      () => {
        alert("Subscription failed. Please try again.");
        this.teardown();
      }
    );
  }

  /**
   * Block document-level navigation while modal is open
   */
  blockDocNav = (e) => {
    if (this.modal.contains(e.target)) return;
    if (this.container.contains(e.target)) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") {
        e.stopImmediatePropagation();
      }
    }
  };

  /**
   * Cleanup listeners, restore link, and remove modal
   */
  teardown = () => {
    ["click", "touchstart", "pointerdown"].forEach((ev) => {
      document.removeEventListener(ev, this.blockDocNav, { capture: true });
    });
    if (this.wrapLink && this.savedHref) {
      this.wrapLink.setAttribute("href", this.savedHref);
    }
    if (this.modal && this.modal.parentNode) {
      this.modal.remove();
    }
  };

  /**
   * Disable wrapping link navigation and add document blockers
   */
  preventDefaultNav() {
    this.wrapLink =
      this.container.closest("a[href]") ||
      this.container.querySelector(":scope > a[href]");
    this.savedHref = null;
    if (this.wrapLink) {
      this.savedHref = this.wrapLink.getAttribute("href");
      this.wrapLink.removeAttribute("href");
    }
    ["click", "touchstart", "pointerdown"].forEach((ev) => {
      document.addEventListener(ev, this.blockDocNav, {
        capture: true,
        passive: false,
      });
    });
  }
}
